import React, { useState } from "react";
import { 
  Box, 
  Button, 
  Typography, 
  IconButton, 
  Radio, 
  RadioGroup, 
  FormControlLabel, 
  TextField,
  Snackbar,
  CircularProgress
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import "./index.css"; 

const DOMAIN = process.env.REACT_APP_DOMAIN;

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const Feedback = () => {
  const [rating, setRating] = useState(3); 
  const [feedback, setFeedback] = useState("");
  const [followUp, setFollowUp] = useState("yes");
  const [loading, setLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const emojis = [
    { value: 1, label: "😢" },
    { value: 2, label: "🙁" },
    { value: 3, label: "😐" },
    { value: 4, label: "🙂" },
    { value: 5, label: "😀" },
  ];

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${DOMAIN}/api/email/feedback`,
        { rating, feedback, followUp },
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("token")}`,
          },
        }
      );
      setOpenSnackbar(true);
      setFeedback("");
      setRating(3);
    } catch (err) {
      console.error(err);
      alert("Error sending feedback. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#f8fafc", 
        p: 2,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "500px",
          bgcolor: "#ffffff",
          borderRadius: 4, 
          boxShadow: "0 20px 50px -12px rgba(130, 87, 230, 0.15)",
          p: { xs: 3, md: 5 },
          position: "relative",
          border: "1px solid rgba(226, 232, 240, 0.7)",
        }}
      >
        {/* Close Button */}
        <IconButton
          sx={{ position: "absolute", top: 16, right: 16, color: "#94a3b8" }}
        >
          <CloseIcon />
        </IconButton>

        {/* Heading Section */}
        <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a", mb: 0.5 }}>
          Give feedback
        </Typography>
        <Typography variant="body2" sx={{ color: "#64748b", mb: 4, fontWeight: 500 }}>
          What do you think of the Markoknow experience?
        </Typography>

        {/* Emoji Rating Row */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 5, px: 2 }}>
          {emojis.map((emoji) => (
            <Box
              key={emoji.value}
              onClick={() => setRating(emoji.value)}
              sx={{
                fontSize: "2rem",
                cursor: "pointer",
                width: 55,
                height: 55,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
                transition: "all 0.2s ease",
                bgcolor: rating === emoji.value ? "rgba(130, 87, 230, 0.1)" : "transparent",
                border: rating === emoji.value ? "2px solid #8257E6" : "2px solid transparent",
                "&:hover": { transform: "scale(1.15)" },
              }}
            >
              {emoji.label}
            </Box>
          ))}
        </Box>

        {/* Feedback Textarea */}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: "#1e293b" }}>
          Do you have any thoughts you'd like to share?
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          placeholder="I think the platform is..."
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          sx={{
            mb: 4,
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
              bgcolor: "#fcfcfc",
            },
          }}
        />

        {/* Follow up Section */}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "#1e293b" }}>
          May we follow you up on your feedback?
        </Typography>
        <RadioGroup
          row
          value={followUp}
          onChange={(e) => setFollowUp(e.target.value)}
          sx={{ mb: 4 }}
        >
          <FormControlLabel 
            value="yes" 
            control={<Radio sx={{ color: "#8257E6", '&.Mui-checked': { color: "#8257E6" } }} />} 
            label={<Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>Yes</Typography>} 
          />
          <FormControlLabel 
            value="no" 
            control={<Radio sx={{ color: "#8257E6", '&.Mui-checked': { color: "#8257E6" } }} />} 
            label={<Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>No</Typography>} 
          />
        </RadioGroup>

        {/* Actions */}
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              bgcolor: "#8257E6",
              fontWeight: 700,
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              boxShadow: "0 10px 20px -5px rgba(130, 87, 230, 0.3)",
              "&:hover": { bgcolor: "#6D48C4" },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : "Send"}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            sx={{
              borderColor: "#e2e8f0",
              color: "#64748b",
              fontWeight: 700,
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f8fafc" },
            }}
          >
            Cancel
          </Button>
        </Box>
      </Box>

      {/* Success Notification */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success">
          Thank you for your feedback! ✅
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Feedback;